import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Search, Send } from "lucide-react";
import { adminApi, StatusBadge } from "@/lib/adminApi";

const TITLES = { drafts: "Drafts", scheduled: "Content Calendar" };

const AdminArticles = ({ filterStatus }) => {
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = () => adminApi(filterStatus ? `/admin/articles?status=${filterStatus}` : "/admin/articles")
    .then(setArticles)
    .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const publishDue = async () => {
    try {
      const { published } = await adminApi("/admin/publish-scheduled", { method: "POST" });
      toast.success(published ? `${published} article(s) published` : "Nothing due yet");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const filtered = articles.filter((a) => (a.title || "").toLowerCase().includes(search.toLowerCase()));
  const title = TITLES[filterStatus] || "All Articles";

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="articles-heading">{title}</h1>
          <p className="text-sm text-[var(--text-secondary)]">{filtered.length} articles</p>
        </div>
        <div className="flex items-center gap-2">
          {filterStatus === "scheduled" && (
            <button onClick={publishDue} className="flex items-center gap-2 border-glow text-white px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 transition-colors" data-testid="publish-due-btn">
              <Send className="w-4 h-4" /> Publish due now
            </button>
          )}
          <Link to="/admin/articles/new" className="flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all" data-testid="new-article-btn">
            <Plus className="w-4 h-4" /> New Article
          </Link>
        </div>
      </div>
      {filterStatus === "scheduled" && (
        <p className="text-xs text-[var(--text-secondary)] -mt-5 mb-6" data-testid="schedule-note">Articles set to <span className="text-white">Scheduled</span> with a date auto-publish every day at 6:00 AM (US Eastern) once their date arrives.</p>
      )}

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
        <input type="text" placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-[var(--bg-card)] border-glow rounded-lg text-sm text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="admin-articles-search" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[var(--bg-card)] border-glow rounded-xl p-12 text-center" data-testid="articles-empty">
          <FileText className="w-10 h-10 text-[var(--text-secondary)]/30 mx-auto mb-3" />
          <p className="text-[var(--text-secondary)]">{filterStatus === "scheduled" ? "Nothing scheduled yet" : "No articles yet"}</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-card)] border-glow rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[rgba(124,58,237,0.1)]">
                <th className="text-left text-xs text-[var(--text-secondary)] uppercase tracking-wider py-3 px-5 font-medium">Title</th>
                <th className="text-left text-xs text-[var(--text-secondary)] uppercase tracking-wider py-3 px-4 font-medium">Category</th>
                <th className="text-left text-xs text-[var(--text-secondary)] uppercase tracking-wider py-3 px-4 font-medium">Status</th>
                <th className="text-left text-xs text-[var(--text-secondary)] uppercase tracking-wider py-3 px-4 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a._id} onClick={() => navigate(`/admin/articles/${a._id}/edit`)} className="border-b border-[rgba(124,58,237,0.05)] hover:bg-[var(--accent-violet)]/5 cursor-pointer transition-colors" data-testid={`article-row-${a._id}`}>
                  <td className="py-3.5 px-5">
                    <p className="text-sm text-white font-medium truncate max-w-md">{a.title}</p>
                    {a.subtitle && <p className="text-xs text-[var(--text-secondary)] truncate max-w-md">{a.subtitle}</p>}
                  </td>
                  <td className="py-3.5 px-4"><span className="text-xs text-[var(--text-secondary)]">{a.category || "—"}</span></td>
                  <td className="py-3.5 px-4">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="py-3.5 px-4 text-xs text-[var(--text-secondary)]">
                    {filterStatus === "scheduled" && a.scheduled_date ? `For ${new Date(a.scheduled_date).toLocaleDateString()}` : a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminArticles;
